/* eslint-disable @typescript-eslint/ban-ts-comment */
import type {
  OperatingParameters,
  XrefPump
} from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { DestructuredScenario } from "@/composables/useDestructureScenario";
import { getHypotheticalScenarioCalculations } from "../single/getHypotheticals";
import type {
  ChartContext,
  HypotheticalFrequency,
  PumpTornadoChartContext,
  SinglePumpChartContext
} from "../d";
import { PoundsPerCubicFoot } from "@/libs/units/density";
import type { ConvertableUnit } from "@/libs/units";
import { getFrequencyRatio } from "@/libs/calculations/pumps/pumpData";
import {
  PoundsPerSquareInchAbsolute,
  PoundsPerSquareInchDifferential,
  PoundsPerSquareInchGauge
} from "@/libs/units/pressure";
import { BarrelsPerDay } from "@/libs/units/flowRate";
import { LargeLengthUnits } from "@/libs/units/length";
import { HorsePower } from "@/libs/units/power";

const buildChartContext = (
  calcs: ScenarioCalculations,
  pump: XrefPump,
  forTaperedPump = false,
  useEvaluationOverrides = false
): ChartContext | undefined => {
  if (!pump) return undefined;
  const destructuredScenario = calcs.destructuredScenario;
  // Get RoRs for the pump in question
  const rorThresholds = {
    rorMin:
      calcs.pumpCalculations.individual.value[pump.id]?.xrefPump.r_pump
        ?.min_rate_standard_pump_bpd || 0,
    rorMax:
      calcs.pumpCalculations.individual.value[pump.id]?.xrefPump.r_pump
        ?.max_rate_standard_pump_bpd || 0,
    rorBep:
      calcs.pumpCalculations.individual.value[pump.id]?.xrefPump.r_pump
        ?.bep_rate_standard_pump_bpd || 0
  };

  const parameters: OperatingParameters = // @ts-ignore
    destructuredScenario.parameters?.value as unknown as OperatingParameters;
  if (!parameters) return undefined;

  // Setup pump array for context - SinglePumpChart and tornado charts use
  // aggregate pump data and need to iterate over all pumps in the pump stack
  // but tapered charts only use data for the single pump being graphed
  const _pumps = forTaperedPump ? [pump] : calcs.ascendingOrderPumps.value;

  // Calculate actual frequency ratio for the scenario
  const frequencyRatio = getFrequencyRatio(
    calcs.pumpCalculations.aggregate.operatingRPM.value,
    calcs.isScenarioEspShore.value
  );

  // Average and discharge fluid densities are needed for single/pump charts
  const averageFluidDensityLBMFT3 =
    calcs.pumpCalculations.aggregate.averageFluidDensityLBMFT3.value;
  const fluidDensitySpGr = PoundsPerCubicFoot.toStandardUnit(
    averageFluidDensityLBMFT3
  );
  const dischargeFluidDensityLBMFT3 = calcs.dischargeFluidDensityLBMFT3.value;

  // Find pump intake/discharge for our pump. It will use the primary pump
  // for the single pump charts
  const pumpIntakeFlowRateBBLD =
    calcs.pumpCalculations.individual.value[pump.id]?.intakeStageCalculations
      ?.intakeFluidPropertiesContext.flowRatesInSitu?.fluidFlowRateBBLD ?? 0;
  const pumpDischargeFlowRateBBLD =
    calcs.pumpCalculations.individual.value[pump.id]?.dischargeStageCalculations
      ?.dischargeFluidPropertiesContext.flowRatesInSitu?.fluidFlowRateBBLD ?? 0;

  const context: ChartContext = {
    useEvaluationOverrides,
    maxFlowRateBBLD: calcs.maxFlowRateBBLD.value,
    ascendingOrderPumps: _pumps,
    liquidFlowRateSTBD: calcs.surfaceFlowRates.value.liquidFlowRateSTBD,
    pumpIntakeFlowRateBBLD,
    pumpDischargeFlowRateBBLD,
    pumpHz: calcs.pumpHz.value,
    operatingRPM: calcs.pumpCalculations.aggregate.operatingRPM.value,
    frequencyRatio,
    gasHeadRerateRatio:
      calcs.pumpCalculations.aggregate.gasHeadRerateRatio.value,
    rorMin: rorThresholds.rorMin,
    rorMax: rorThresholds.rorMax,
    rorBep: rorThresholds.rorBep,
    pumpWearHeadCorrectionRatio: parameters.pump_wear_head_correction_ratio,
    pumpWearBHPCorrectionRatio:
      parameters.pump_wear_brake_horsepower_correction_ratio,
    fluidDensitySpGr,
    averageFluidDensityLBMFT3,
    dischargeFluidDensityLBMFT3,
    destructuredScenario,
    scenarioCalculations: calcs,
    pumpRequiredBrakeHP:
      calcs.pumpCalculations.aggregate.requiredPumpBrakeHP.value,
    isScenarioEspShore: calcs.isScenarioEspShore.value
  };

  return context;
};

// This builds context for single pump chart
// (single freq, pump stack/primary pump)
export const buildSinglePumpChartContext = (
  calcs: ScenarioCalculations,
  targetLiquidFlowUnit: ConvertableUnit,
  targetLargeLengthUnit: ConvertableUnit,
  targetPressureUnit: ConvertableUnit,
  targetPowerUnit: ConvertableUnit
): SinglePumpChartContext | undefined => {
  // We only care about the primary pump with this chart data
  const primaryPump = calcs.primaryPump.value;
  if (!primaryPump) return undefined;
  const chartContext = buildChartContext(calcs, primaryPump, false, false);
  if (!chartContext) return undefined;
  const destructuredScenario = calcs.destructuredScenario;
  if (!destructuredScenario) return undefined;

  // For a single pump chart we need to find the lowest graph max since
  // this is using aggregate data
  const graphMax = Math.min(
    ...calcs.ascendingOrderPumps.value.map(
      xrefPump => calcs.pumpCalculations.individual.value[xrefPump.id].graphMax
    )
  );
  // Use our RORs here for SinglePumpChart since we already have these
  // calculated for the actual frequency,whereas the tornado and tapered
  // charts need to calculate these for multiple frequencies
  const rorMin =
    calcs.pumpCalculations.individual.value[primaryPump.id]?.rorMin || 0;
  const rorMax =
    calcs.pumpCalculations.individual.value[primaryPump.id]?.rorMax || 0;
  const rorBep =
    calcs.pumpCalculations.individual.value[primaryPump.id]?.rorBep || 0;

  // Setup flow rates for hypothetical calculation sets
  const hypotheticals = getHypotheticalScenarioCalculations(
    destructuredScenario as DestructuredScenario,
    graphMax,
    chartContext.pumpIntakeFlowRateBBLD
  );

  // I CANNOT GET THIS TYPESCRIPT ERROR TO GO AWAY UNLESS I IGNORE IT
  const parameters: OperatingParameters = // @ts-ignore
    destructuredScenario.parameters?.value as unknown as OperatingParameters;
  if (!parameters) return undefined;

  // Get our base and target Y units setup for use in the charts and conversions
  const baseYUnit = chartContext.isScenarioEspShore
    ? LargeLengthUnits.Feet
    : PoundsPerSquareInchAbsolute;
  // Note: HPS charts target pressure units are always in absolute
  // We do not pass in absolute in case the component is using the target
  // units else where like in startup reports where the user would want to see PSIG
  const targetYUnit = chartContext.isScenarioEspShore
    ? targetLargeLengthUnit
    : PoundsPerSquareInchAbsolute;

  // If target Y unit is pressure, then it could be PSIA or PSIG
  // If either of those then we need the chart to say it is PSID
  let targetYUnitForDisplay = targetYUnit;
  if (
    targetYUnit === PoundsPerSquareInchAbsolute ||
    targetYUnit === PoundsPerSquareInchGauge
  ) {
    targetYUnitForDisplay = PoundsPerSquareInchDifferential;
  }
  // Setup our x and y unit labels (we know xunit is always flow rate in this chart
  // but y unit could be pressure or head depending on scenario type)
  const targetXUnitLabel =
    targetLiquidFlowUnit.inSituLabel ||
    targetLiquidFlowUnit.shortLabel ||
    targetLiquidFlowUnit.label;
  const targetYUnitLabel =
    targetYUnitForDisplay.inSituLabel ||
    targetYUnitForDisplay.shortLabel ||
    targetYUnitForDisplay.label;

  const targetBhpYUnitLabel =
    targetPowerUnit.inSituLabel ||
    targetPowerUnit.shortLabel ||
    targetPowerUnit.label;
  // Setup npsh chart y unit and labels since it's always in length units
  // whereas the main chart could be in pressure or length units depending on scenario type
  const baseNpshYUnit = LargeLengthUnits.Feet;
  const targetNpshYUnit = targetLargeLengthUnit;
  const targetNpshYUnitLabel =
    targetLargeLengthUnit.inSituLabel ||
    targetLargeLengthUnit.shortLabel ||
    targetLargeLengthUnit.label;

  const singlePumpChartContext: SinglePumpChartContext = {
    ...chartContext,
    baseXUnit: BarrelsPerDay,
    targetXUnit: targetLiquidFlowUnit,
    targetXUnitLabel,
    baseYUnit,
    targetYUnit,
    targetYUnitLabel,
    targetYUnitForDisplay,
    baseBhpYUnit: HorsePower,
    targetBhpYUnit: targetPowerUnit,
    targetBhpYUnitLabel,
    baseNpshYUnit,
    targetNpshYUnit,
    targetNpshYUnitLabel,
    rorBep,
    rorMin,
    rorMax,
    graphMax,
    hypotheticals,
    npsha: calcs.selectedNPSHaFt.value
  };

  return singlePumpChartContext;
};

export const buildTornadoPumpChartContext = (
  minFrequency: number,
  maxFrequency: number,
  calcs: ScenarioCalculations,
  targetLiquidFlowUnit: ConvertableUnit,
  targetLargeLengthUnit: ConvertableUnit,
  targetPressureUnit: ConvertableUnit,
  targetPowerUnit: ConvertableUnit,
  useEvaluationOverrides = false
): PumpTornadoChartContext | undefined => {
  // We only care about the primary pump with this chart data
  const primaryPump = calcs.primaryPump.value;
  if (!primaryPump) return undefined;
  const chartContext = buildChartContext(
    calcs,
    primaryPump,
    false,
    useEvaluationOverrides
  );
  if (!chartContext) return undefined;

  // // Maximum of X is set to the lowest available graphMax in ROR of highest available frequency
  const getGraphMax = (
    hypotheticalPumpFrequenciesHz: HypotheticalFrequency[],
    primaryPump: XrefPump
  ) => {
    if (
      !hypotheticalPumpFrequenciesHz ||
      hypotheticalPumpFrequenciesHz.length === 0
    )
      return 0;
    const frequencyRatio =
      hypotheticalPumpFrequenciesHz[hypotheticalPumpFrequenciesHz.length - 1]
        .frequencyRatio;

    const graphMax =
      (primaryPump.r_pump?.max_graph_rate_standard_pump_bpd || 0) *
      frequencyRatio *
      primaryPump.rate_correction_ratio;

    return graphMax;
  };

  const hypotheticalPumpFrequenciesHz: HypotheticalFrequency[] = [];

  for (let f = minFrequency; f <= maxFrequency; f += 5) {
    // Need to divide freq by actual pump hz
    const rpm = (f / chartContext.pumpHz) * chartContext.operatingRPM;
    hypotheticalPumpFrequenciesHz.push({
      frequency: f,
      rpm,
      frequencyRatio: getFrequencyRatio(rpm, chartContext.isScenarioEspShore)
    });
  }
  // This was added to ensure the tdh/bhp lines run right through
  // the operating point on the chart all the time since this freq
  // line set includes the exact frequency (the same one the operating
  // points also use)
  const hypotheticalPumpFrequenciesIncludeRealHz =
    hypotheticalPumpFrequenciesHz.slice();
  hypotheticalPumpFrequenciesIncludeRealHz.push({
    frequency: calcs.pumpHz.value,
    rpm: calcs.pumpCalculations.aggregate.operatingRPM.value,
    frequencyRatio: chartContext.frequencyRatio
  });

  const graphMax = getGraphMax(hypotheticalPumpFrequenciesHz, primaryPump);
  const hypotheticalRatesOverRealHz = getHypotheticalScenarioCalculations(
    chartContext.destructuredScenario as DestructuredScenario,
    graphMax,
    chartContext.pumpIntakeFlowRateBBLD
  );
  const hypotheticalFlowRates = [
    ...Array.from({ length: 40 }, (_, i) => (graphMax / 40) * (i + 1)),
    chartContext.liquidFlowRateSTBD,
    chartContext.pumpIntakeFlowRateBBLD
  ].sort((a, b) => a - b);

  const isRateCorrected = !!calcs.descendingOrderPumps.value.find(
    p => [0, 1].indexOf(p.rate_correction_ratio) === -1
  );

  // Setup our x and y units for both head and bhp tornado charts
  const targetXUnitLabel =
    targetLiquidFlowUnit.inSituLabel ||
    targetLiquidFlowUnit.shortLabel ||
    targetLiquidFlowUnit.label;

  const baseTdhYUnit = chartContext.isScenarioEspShore
    ? LargeLengthUnits.Feet
    : PoundsPerSquareInchAbsolute;
  // Note: HPS charts target pressure units are always in absolute
  // We do not pass in absolute in case the component is using the target
  // units else where like in startup reports where the user would want to see PSIG
  const targetTdhYUnit = chartContext.isScenarioEspShore
    ? targetLargeLengthUnit
    : PoundsPerSquareInchAbsolute;
  // If target Y unit is pressure, then it could be PSIA or PSIG
  // If either of those then we need the chart to say it is PSID
  let targetTdhYUnitForDisplay = targetTdhYUnit;
  if (
    targetTdhYUnit === PoundsPerSquareInchAbsolute ||
    targetTdhYUnit === PoundsPerSquareInchGauge
  ) {
    targetTdhYUnitForDisplay = PoundsPerSquareInchDifferential;
  }
  const targetTdhYUnitLabel =
    targetTdhYUnitForDisplay.inSituLabel ||
    targetTdhYUnitForDisplay.shortLabel ||
    targetTdhYUnitForDisplay.label;

  const targetBhpYUnitLabel =
    targetPowerUnit.inSituLabel ||
    targetPowerUnit.shortLabel ||
    targetPowerUnit.label;

  const tornadoPumpChartContext: PumpTornadoChartContext = {
    ...chartContext,
    pump: primaryPump,
    hypotheticalRatesOverRealHz,
    hypotheticalFlowRates,
    hypotheticalPumpFrequenciesHz,
    hypotheticalPumpFrequenciesIncludeRealHz,
    graphMax,
    motorPoles: calcs.motorCalculations.motorPoles.value,
    requiredTotalDynamicHeadFt:
      calcs.pumpCalculations.aggregate.requiredTotalDynamicHeadFt.value,
    requiredDifferentialPressurePSIA:
      calcs.pumpCalculations.aggregate.differentialPressurePSIA.value,
    producedTotalDynamicHeadFt:
      calcs.pumpCalculations.aggregate.producedTotalDynamicHeadFt.value,
    totalRequiredBrakeHP: calcs.motorCalculations.totalRequiredBrakeHP.value,
    pumpIntakeFlowRateBBLD:
      calcs.pumpCalculations.aggregate.intakeFlowRateSTBD.value,
    pumpDischargeFlowRateBBLD:
      calcs.pumpCalculations.aggregate.dischargeFlowRateSTBD.value,
    baseXUnit: BarrelsPerDay,
    targetXUnit: targetLiquidFlowUnit,
    targetXUnitLabel,
    baseTdhYUnit,
    targetTdhYUnit,
    targetTdhYUnitForDisplay,
    targetTdhYUnitLabel,
    baseBhpYUnit: HorsePower,
    targetBhpYUnit: targetPowerUnit,
    targetBhpYUnitLabel,
    isRateCorrected
  };

  return tornadoPumpChartContext;
};

// We care about multi freqs and a single pump passed into here
export const buildTaperedPumpChartContext = (
  minFrequency: number,
  maxFrequency: number,
  pump: XrefPump,
  calcs: ScenarioCalculations,
  targetLiquidFlowUnit: ConvertableUnit,
  targetLargeLengthUnit: ConvertableUnit,
  targetPressureUnit: ConvertableUnit,
  targetPowerUnit: ConvertableUnit,
  useEvaluationOverrides = false
): PumpTornadoChartContext | undefined => {
  if (!pump) return undefined;
  const chartContext = buildChartContext(
    calcs,
    pump,
    true,
    useEvaluationOverrides
  );
  if (!chartContext) return undefined;

  // // Maximum of X is set to the lowest available graphMax in ROR of highest available frequency
  const getGraphMax = (
    hypotheticalPumpFrequenciesHz: HypotheticalFrequency[],
    pump: XrefPump
  ) => {
    if (
      !hypotheticalPumpFrequenciesHz ||
      hypotheticalPumpFrequenciesHz.length === 0
    )
      return 0;
    const frequencyRatio =
      hypotheticalPumpFrequenciesHz[hypotheticalPumpFrequenciesHz.length - 1]
        .frequencyRatio;

    const graphMax =
      (pump.r_pump?.max_graph_rate_standard_pump_bpd || 0) * frequencyRatio;

    return graphMax;
  };

  const hypotheticalPumpFrequenciesHz: HypotheticalFrequency[] = [];

  for (let f = minFrequency; f <= maxFrequency; f += 5) {
    // Need to divide freq by actual pump hz for adjustment
    const rpm = (f / chartContext.pumpHz) * chartContext.operatingRPM;
    hypotheticalPumpFrequenciesHz.push({
      frequency: f,
      rpm,
      frequencyRatio: getFrequencyRatio(rpm, chartContext.isScenarioEspShore)
    });
  }
  // This was added to ensure the tdh/bhp lines run right through
  // the operating point on the chart all the time since this freq
  // line set includes the exact frequency (the same one the operating
  // points also use)
  const hypotheticalPumpFrequenciesIncludeRealHz =
    hypotheticalPumpFrequenciesHz.slice();
  hypotheticalPumpFrequenciesIncludeRealHz.push({
    frequency: calcs.pumpHz.value,
    rpm: calcs.pumpCalculations.aggregate.operatingRPM.value,
    frequencyRatio: chartContext.frequencyRatio
  });

  const graphMax = getGraphMax(hypotheticalPumpFrequenciesHz, pump);
  const hypotheticalFlowRates = Array.from(
    { length: 40 },
    (_, i) => (graphMax / 40) * (i + 1)
  );

  // Setup our x and y units
  const targetXUnitLabel =
    targetLiquidFlowUnit.inSituLabel ||
    targetLiquidFlowUnit.shortLabel ||
    targetLiquidFlowUnit.label;

  const baseTdhYUnit = chartContext.isScenarioEspShore
    ? LargeLengthUnits.Feet
    : PoundsPerSquareInchAbsolute;

  // Note: HPS charts target pressure units are always in absolute
  // We do not pass in absolute in case the component is using the target
  // units else where like in startup reports where the user would want to see PSIG
  const targetTdhYUnit = chartContext.isScenarioEspShore
    ? targetLargeLengthUnit
    : PoundsPerSquareInchAbsolute;

  // If target Y unit is pressure, then it could be PSIA or PSIG
  // If either of those then we need the chart to say it is PSID
  let targetTdhYUnitForDisplay = targetTdhYUnit;
  if (
    targetTdhYUnit === PoundsPerSquareInchAbsolute ||
    targetTdhYUnit === PoundsPerSquareInchGauge
  ) {
    targetTdhYUnitForDisplay = PoundsPerSquareInchDifferential;
  }
  const targetTdhYUnitLabel =
    targetTdhYUnitForDisplay.inSituLabel ||
    targetTdhYUnitForDisplay.shortLabel ||
    targetTdhYUnitForDisplay.label;

  // I'm not sure this is used in tapered pumps but kept in case
  // we ever need to make a bhp tapered chart
  const targetBhpYUnitLabel =
    targetPowerUnit.inSituLabel ||
    targetPowerUnit.shortLabel ||
    targetPowerUnit.label;

  const tornadoPumpChartContext: PumpTornadoChartContext = {
    ...chartContext,
    pump,
    hypotheticalFlowRates,
    hypotheticalPumpFrequenciesHz,
    hypotheticalPumpFrequenciesIncludeRealHz,
    graphMax,
    motorPoles: calcs.motorCalculations.motorPoles.value,
    requiredTotalDynamicHeadFt: 0,
    requiredDifferentialPressurePSIA: 0,
    producedTotalDynamicHeadFt:
      calcs.pumpCalculations.individual.value[pump.id]?.totalDynamicHeadFt,
    totalRequiredBrakeHP: calcs.motorCalculations.totalRequiredBrakeHP.value,
    pumpRequiredBrakeHP:
      calcs.pumpCalculations.individual.value[pump.id]?.totalBrakeHP,
    pumpIntakeFlowRateBBLD:
      calcs.pumpCalculations.individual.value[pump.id]?.intakeStageCalculations
        ?.intakeFluidPropertiesContext.flowRatesInSitu?.fluidFlowRateBBLD || 0,
    pumpDischargeFlowRateBBLD:
      calcs.pumpCalculations.individual.value[pump.id]
        ?.dischargeStageCalculations?.dischargeFluidPropertiesContext
        .flowRatesInSitu?.fluidFlowRateBBLD || 0,
    baseXUnit: BarrelsPerDay,
    targetXUnit: targetLiquidFlowUnit,
    targetXUnitLabel,
    baseTdhYUnit,
    targetTdhYUnit,
    targetTdhYUnitForDisplay,
    targetTdhYUnitLabel,
    baseBhpYUnit: HorsePower,
    targetBhpYUnit: targetPowerUnit,
    targetBhpYUnitLabel,
    isRateCorrected:
      pump.rate_correction_ratio !== 0 && pump.rate_correction_ratio !== 1
  };

  return tornadoPumpChartContext;
};
